import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import {
  isEmptyObjectOrHasEmptyValues,
  isNotEmpty,
  PreciseNumber,
  toBoolean,
  unique,
  unsubscribe,
} from '@fcom/core/utils';
import { finShare, snapshot } from '@fcom/rx';
import {
  Amount,
  GroupedServices,
  NO_VARIANTS,
  SelectionsPerTraveler,
  ServiceCatalogService,
  ServicePassenger,
  ServicesPerTraveler,
  TravelerService,
} from '@fcom/dapi/interfaces';
import {
  Category,
  FinnairBoundItem,
  FinnairItineraryItemFlight,
  FinnairPassengerItem,
  FinnairPassengerServiceSelectionItem,
  FinnairServiceItem,
  PaxLevelQuota,
  SubCategory,
} from '@fcom/dapi/api/models';
import { LoaderType } from '@fcom/common/interfaces';
import {
  AspectRatios,
  ButtonMode,
  ButtonTheme,
  IconPosition,
  ModalButtons,
  NotificationTheme,
  ChipTheme,
  TagTheme,
} from '@fcom/ui-components';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { NON_SCHENGEN_LOUNGES } from '@fcom/booking/utils';
import { MediaQueryService, ToasterService } from '@fcom/common/services';
import { LanguageService } from '@fcom/ui-translate';

import {
  findServiceItemsForPassengerForFragment,
  getFilteredTravelers$,
  getPassengerTierLevel,
  getServicesNotifications,
  sumOfPricesForTravelersServiceSelection,
} from '../../utils';
import { QuantityForFragmentAndPassenger, ServicesActions, serviceSelectionsForFragment } from '../../../../store';
import {
  ConfirmModal,
  ConfirmModalData,
  EligibilitiesForFragments,
  ExtendedService,
  Notification,
  ProductInputType,
  QuotaType,
} from '../../interfaces';
import { asTravelerService } from '../../../../store/utils/services.utils';
import { getGroupedServices } from '../../../../utils/common-booking.utils';

export enum SelectionState {
  LOADING,
  HAS_SERVICES,
  NO_SERVICES,
}

export interface ConfirmModalTranslationKeys {
  title: string;
  firstContentParagraph: string;
  secondContentParagraph: string;
  closeButton: string;
  continueButton: string;
}

type TravelerServices = {
  traveler: ServicePassenger;
  services: ExtendedService[];
  selectedServices$: Observable<ExtendedService[]>;
  hasServiceSelected$: Observable<boolean>;
  additionalInfoBlockUrl: string | undefined;
  filters: SubCategoryFilter[];
  selectedFilter$: BehaviorSubject<SubCategoryFilter>;
  combinedGroupedServices: GroupedServices[];
  useConfirmModalBeforeSelectionRemoval: boolean;
};

interface ProductConfig {
  notSelectedVariant?: string;
  selectServiceKey: string;
  toppedQuotaKey?: string;
  quotaLimitKey?: string;
  noQuotaLeftKey?: string;
  blockSelectionNotification?: string;
  alreadyPurchased: string;
}

const CONFIG: Record<string, ProductConfig> = {
  [Category.WIFI]: {
    notSelectedVariant: '',
    selectServiceKey: 'ancillaries.wifi.selectWifi',
    alreadyPurchased: 'ancillaries.wifi.alreadyPurchased',
  },
  [Category.MEAL]: {
    notSelectedVariant: '',
    selectServiceKey: 'ancillaries.selectMeals',
    alreadyPurchased: 'ancillaries.meals.alreadyPurchased',
  },
  [Category.LOUNGE]: {
    selectServiceKey: 'ancillaries.selectLounge',
    alreadyPurchased: 'ancillaries.lounge.alreadyPurchased',
  },
  [Category.SPORT]: {
    selectServiceKey: 'ancillaries.selectSport',
    alreadyPurchased: 'ancillaries.sport.alreadyPurchased',
  },
  [Category.CABIN_BAGGAGE]: {
    selectServiceKey: 'ancillaries.cabinBaggage.title',
    alreadyPurchased: 'ancillaries.cabinBaggage.alreadyPurchased',
  },
  [Category.BAGGAGE]: {
    selectServiceKey: 'ancillaries.bags.extraTitle',
    alreadyPurchased: 'ancillaries.bags.alreadyPurchased',
  },
  [Category.PET]: {
    selectServiceKey: 'ancillaries.selectPet',
    quotaLimitKey: 'ancillaries.pet.quota.quotaLimit',
    toppedQuotaKey: 'ancillaries.pet.quota.toppedQuota',
    noQuotaLeftKey: 'ancillaries.pet.quota.noQuotaLeft',
    blockSelectionNotification: 'ancillaries.pet.hasIncompatibleSeat',
    alreadyPurchased: 'ancillaries.pet.alreadyPurchased',
  },
};

type SelectionWithCategoryAndFragment = [
  SelectionsPerTraveler<TravelerService[]>,
  Category,
  FinnairItineraryItemFlight | FinnairBoundItem,
];

interface TravelerSelections {
  [variantOrRadio: string]: string | boolean | number;
}
type TravelerForm = FormGroup<{ [variantOrRadio: string]: FormControl<boolean | string | number> }>;

type SubCategoryFilter = SubCategory | 'all' | 'none';

const COMMON_TOASTER_ID = 'COMMON-TOASTER-ID';

@Component({
  selector: 'fin-common-product-selection',
  templateUrl: 'common-product-selection.component.html',
  styleUrls: ['common-product-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommonProductSelectionComponent implements OnInit, OnDestroy {
  readonly ButtonMode = ButtonMode;
  readonly ButtonTheme = ButtonTheme;
  readonly Category = Category;
  readonly CONFIG = CONFIG;
  readonly IconPosition = IconPosition;
  readonly LoaderType = LoaderType;
  readonly ModalButtons = ModalButtons;
  readonly NotificationTheme = NotificationTheme;
  readonly QuotaType = QuotaType;
  readonly SelectionState = SelectionState;
  readonly SubCategory = SubCategory;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TagTheme = TagTheme;
  readonly ChipTheme = ChipTheme;

  @Input()
  servicesReady$: Observable<boolean>;

  @Input()
  category$: Observable<Category>;

  @Input()
  flightOrBound$: Observable<FinnairItineraryItemFlight | FinnairBoundItem>;

  @Input()
  services$: Observable<ServicesPerTraveler>;

  @Input()
  includedServices$: Observable<FinnairServiceItem | null> = of(null);

  @Input()
  combinedServices$: Observable<FinnairServiceItem | null> = of(null);

  @Input()
  travelers$: Observable<FinnairPassengerItem[]>;

  @Input()
  initialSelectedTravelerId$: Observable<string> = of(undefined);

  @Input()
  showConfirmationModalBeforeSelectionForPassengers$: Observable<string[]> = of([]);

  @Input()
  showConfirmationModalBeforeSelectionRemovalForPassengers$: Observable<string[]> = of([]);

  @Input()
  blockSelectionNotification$: Observable<string[]> = of([]);

  @Input()
  confirmModalData$: BehaviorSubject<ConfirmModal | undefined> = new BehaviorSubject<ConfirmModal>({ open: false });

  @Input()
  availability$: Observable<EligibilitiesForFragments> = of({});

  @Input()
  serviceQuantityMap$: Observable<QuantityForFragmentAndPassenger> = of({});

  @Input()
  paxLevelQuota$: Observable<PaxLevelQuota> = of({ combinedQuota: 0, variants: [] });

  @Output()
  subtotalChange: EventEmitter<Amount> = new EventEmitter();

  @Output()
  openConfirmModalWithData: EventEmitter<ConfirmModalData> = new EventEmitter<ConfirmModalData>();

  form: FormGroup<{ [travelerId: string]: TravelerForm }>;
  state$: Observable<SelectionState>;
  additionalText: string | undefined;
  travelersAndServices$: Observable<TravelerServices[]>;
  initialTravelerIdSelected$: Observable<string>;
  serviceTabGroupId: string;
  notifications$: Observable<Notification[]>;
  showSportNotification$: Observable<boolean>;
  blockingNotifications$: Observable<Notification[]>;
  aspectRatio$: Observable<keyof typeof AspectRatios>;
  isTabletDown$: Observable<boolean>;
  flightOrBound: FinnairItineraryItemFlight | FinnairBoundItem;
  category: Category;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private store$: Store<BookingAppState>,
    private fb: FormBuilder,
    private mediaQueryService: MediaQueryService,
    private languageService: LanguageService,
    private toasterService: ToasterService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.form = new FormGroup({});

    this.isTabletDown$ = this.mediaQueryService.isBreakpoint$('tablet_down');
    this.aspectRatio$ = this.isTabletDown$.pipe(map((isTabletDown) => (isTabletDown ? '21x9' : '1x1')));

    const baseStream$ = combineLatest([this.category$, this.flightOrBound$]).pipe(
      distinctUntilChanged((a, b) => a[0] === b[0] && a[1].id === b[1].id)
    );
    this.subscriptions.add(
      baseStream$.subscribe(([category, flightOrBound]) => {
        this.category = category;
        this.flightOrBound = flightOrBound;
        this.serviceTabGroupId = `${category}-${flightOrBound.id}-travelers`;
        this.additionalText = this.getAdditionalText(category, flightOrBound);
        this.cdr.markForCheck();
      })
    );

    const filteredTravelers$: Observable<ServicePassenger[]> = getFilteredTravelers$(this.travelers$);

    this.setupForm(baseStream$, filteredTravelers$);
    this.setupTravelersAndServices(baseStream$, filteredTravelers$);

    this.state$ = combineLatest([
      this.servicesReady$.pipe(startWith(false)),
      this.travelersAndServices$.pipe(map(isNotEmpty), startWith(false)),
    ]).pipe(
      map(([servicesReady, hasServices]: [boolean, boolean]) => {
        if (!servicesReady) {
          return SelectionState.LOADING;
        }
        if (hasServices) {
          return SelectionState.HAS_SERVICES;
        }
        return SelectionState.NO_SERVICES;
      }),
      distinctUntilChanged(),
      finShare()
    );

    // == Tabs ==
    this.initialTravelerIdSelected$ = combineLatest([this.travelersAndServices$, this.initialSelectedTravelerId$]).pipe(
      map(([travelersWithServices, initialTravelerId]) => initialTravelerId ?? travelersWithServices[0]?.traveler.id)
    );

    this.notifications$ = combineLatest([this.services$, this.availability$, this.category$, this.flightOrBound$]).pipe(
      map(([services, availability, category, flightOrBound]) =>
        getServicesNotifications(services, availability, category, flightOrBound)
      ),
      finShare()
    );

    this.blockingNotifications$ = this.notifications$.pipe(
      map((notifications) => notifications.filter((n) => n.blockSelection))
    );

    this.showSportNotification$ = this.category$.pipe(
      map((category) => {
        return category === Category.BAGGAGE;
      })
    );
  }

  ngOnDestroy(): void {
    this.toasterService.removeMessage(COMMON_TOASTER_ID);
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  clickInput(event: Event, service: ExtendedService, flightOrBoundId: string, travelerId: string): void {
    if (service.useConfirmModal) {
      event.preventDefault();
      this.openConfirmModalWithData.emit({
        variant: service.variant,
        flightOrBoundId: flightOrBoundId,
        travelerId: travelerId,
      });
    }
  }

  addQuantity(service: ExtendedService, flightOrBoundId: string, travelerId: string): void {
    const currentValue = +this.form.controls[travelerId]?.controls[service.variant].value;
    const quantity = currentValue + 1;

    if (service.useConfirmModal) {
      this.openConfirmModalWithData.emit({
        variant: service.variant,
        quantity,
        flightOrBoundId: flightOrBoundId,
        travelerId: travelerId,
      });
    } else {
      this.form.controls[travelerId]?.controls[service.variant]?.setValue(quantity);
    }
  }

  removeQuantity(service: ExtendedService, flightOrBoundId: string, travelerId: string): void {
    const currentValue = +this.form.controls[travelerId]?.controls[service.variant].value;
    const quantity = currentValue - 1;

    if (service.useConfirmModal) {
      this.openConfirmModalWithData.emit({
        variant: service.variant,
        quantity,
        flightOrBoundId: flightOrBoundId,
        travelerId: travelerId,
      });
    } else {
      this.form.controls[travelerId]?.controls[service.variant]?.setValue(quantity);
    }
  }

  private getAdditionalInfoBlock(services: ExtendedService[], category: Category): string | undefined {
    if (category === Category.LOUNGE) {
      return services.filter((service) => NON_SCHENGEN_LOUNGES.includes(service.variant))?.length > 0
        ? 'fragments.nonSchengenLoungeContent.url'
        : 'fragments.schengenLoungeContent.url';
    }
    return `fragments.services.productInfo.${category}.url`;
  }

  private getAdditionalText(
    category: Category,
    flightOrBound: FinnairItineraryItemFlight | FinnairBoundItem
  ): string | undefined {
    if (category === Category.WIFI) {
      if ((flightOrBound as FinnairItineraryItemFlight).isShortHaul) {
        return 'ancillaries.wifi.shorthaulAdditionalInformation';
      }
      return 'ancillaries.wifi.additionalInformation';
    }
    return undefined;
  }

  private addValuesToFormAfterConfirmModal(confirmData: ConfirmModalData) {
    const formControls = this.form.controls[confirmData.travelerId].controls;
    if (formControls['radio']) {
      formControls['radio'].setValue(confirmData.variant);
    } else if (confirmData.quantity) {
      formControls[confirmData.variant].setValue(confirmData.quantity);
    } else {
      formControls[confirmData.variant].setValue(true);
    }
  }

  private setupForm(
    baseStream$: Observable<[Category, FinnairItineraryItemFlight | FinnairBoundItem]>,
    filteredTravelers$: Observable<ServicePassenger[]>
  ): void {
    this.subscriptions.add(
      this.confirmModalData$
        .pipe(
          withLatestFrom(this.flightOrBound$),
          filter(
            ([data, flightOrBound]) =>
              (data.confirmData?.variant || data.confirmData?.removeSelection) &&
              data.confirmData?.travelerId &&
              flightOrBound.id === data.confirmData?.flightOrBoundId &&
              !data.open
          )
        )
        .subscribe(([{ confirmData }]) =>
          confirmData.removeSelection
            ? this.removeSelections(undefined, confirmData.travelerId, confirmData.flightOrBoundId, false)
            : this.addValuesToFormAfterConfirmModal(confirmData)
        )
    );

    this.subscriptions.add(
      baseStream$
        .pipe(
          switchMap(([category, flightOrBound]) => {
            return combineLatest([
              of(category),
              this.store$.pipe(
                serviceSelectionsForFragment<TravelerService | TravelerService[]>(flightOrBound.id, category)
              ),
              filteredTravelers$.pipe(
                map((travelers) => travelers.map((traveler) => traveler.id)),
                distinctUntilChanged()
              ),
              this.services$,
              this.includedServices$,
            ]).pipe(take(1));
          })
        )
        .subscribe(([category, initialSelections, travelerIds, services, includedServices]) => {
          travelerIds.forEach((travelerId: string) => {
            this.form.removeControl(travelerId, { emitEvent: false });
            const initialServices: TravelerService[] = [].concat(initialSelections?.[travelerId] ?? []);
            const travelerForm: TravelerForm = this.fb.group({});
            if (this.isRadioDisplay()) {
              const includedServicesForTravelerAndFragment = findServiceItemsForPassengerForFragment(
                this.category,
                includedServices,
                travelerId,
                this.flightOrBound.id
              );
              travelerForm.registerControl(
                'radio',
                this.fb.control(
                  includedServicesForTravelerAndFragment?.[0]?.variant ||
                    initialServices?.[0]?.variant ||
                    CONFIG[category]?.notSelectedVariant
                )
              );
            } else {
              const allServices = services?.[travelerId]?.filter((s) => s.isService) ?? [];
              allServices.forEach((is) => {
                const initialSelection = initialServices.find((s) => s.variant === is.variant);
                const initialQuantityOrBoolean =
                  is.maxNumberItems > 1 ? (initialSelection?.quantity ?? 0) : !!initialSelection;
                travelerForm.registerControl(is.variant, this.fb.control(initialQuantityOrBoolean));
              });
            }
            this.form.registerControl(travelerId, travelerForm);
          });
          this.subtotalChange.emit(sumOfPricesForTravelersServiceSelection(initialSelections ?? {}));
        })
    );

    const selections$ = this.form.valueChanges.pipe(
      filter((model) => !isEmptyObjectOrHasEmptyValues(model)),
      withLatestFrom(this.services$),
      map(([model, services]) => {
        return Object.keys(model).reduce((selections: SelectionsPerTraveler<TravelerService[]>, travelerId: string) => {
          const servicesForTraveler = services[travelerId] ?? [];

          const travelerSelections = servicesForTraveler
            .filter((sm: ServiceCatalogService) => {
              if (sm.maxNumberItems > 1) {
                return Number(model[travelerId][sm.variant]) > 0;
              }
              return toBoolean(model[travelerId][sm.variant]) || model[travelerId]['radio'] === sm.variant;
            })
            .map((s) => {
              if (!s.isService) {
                return undefined;
              }
              const quantity = model[travelerId][s.variant];
              return asTravelerService(s, typeof quantity === 'number' ? quantity : 1);
            });

          selections[travelerId] = travelerSelections;
          return selections;
        }, {});
      })
    );

    this.subscriptions.add(
      baseStream$
        .pipe(
          switchMap(([category, flightOrBound]) =>
            selections$.pipe(
              map((selections) => [selections, category, flightOrBound] as SelectionWithCategoryAndFragment)
            )
          ),
          withLatestFrom(this.includedServices$)
        )
        .subscribe(([[selections, category, flightOrBound], includedServices]) => {
          const selectionsToStore = this.isRadioDisplay(category)
            ? Object.entries(selections).reduce((selectionsPerTraveler, [traveledId, selectionsForFragment]) => {
                const servicesForFragments = findServiceItemsForPassengerForFragment(
                  category,
                  includedServices,
                  traveledId,
                  flightOrBound.id
                );

                selectionsPerTraveler[traveledId] = selectionsForFragment.filter((selection) => {
                  return !servicesForFragments.some(
                    ({ variant, modifiable }) => !modifiable && variant === selection.variant
                  );
                });

                return selectionsPerTraveler;
              }, {})
            : selections;

          this.checkAndQueueToasterNotification(category, selectionsToStore);

          this.store$.dispatch(
            ServicesActions.setSelectionsForFragment({
              category: category,
              fragmentId: flightOrBound.id,
              selections: selectionsToStore,
            })
          );
          this.subtotalChange.emit(sumOfPricesForTravelersServiceSelection(selectionsToStore));
        })
    );
  }

  private checkAndQueueToasterNotification(
    category: Category,
    selections: SelectionsPerTraveler<TravelerService[]>
  ): void {
    if (category !== Category.MEAL) {
      return;
    }

    const nextHasSpecialDiet: Record<string, boolean> = Object.entries(selections).reduce(
      (all, [key, selectionsForTraveler]) => {
        all[key] = selectionsForTraveler.some((v) => v.category === SubCategory.SPECIAL_DIET);
        return all;
      },
      {}
    );
    const currentSelection: SelectionsPerTraveler<TravelerService | TravelerService[]> = snapshot(
      this.store$.pipe(serviceSelectionsForFragment(this.flightOrBound.id, this.category))
    );
    const currentHasSpecialDiet: Record<string, boolean> = Object.entries(currentSelection).reduce(
      (all, [key, travelerSelection]: [string, TravelerService | TravelerService[]]) => {
        const selectionsForTraveler = Array.isArray(travelerSelection) ? travelerSelection : [travelerSelection];
        all[key] = selectionsForTraveler.some((v) => v.category === SubCategory.SPECIAL_DIET);
        return all;
      },
      {}
    );

    const showSpecialDietSelected = Object.entries(nextHasSpecialDiet).some(
      ([key, value]) => value && !currentHasSpecialDiet[key]
    );
    const showSpecialDietUnselected = Object.entries(currentHasSpecialDiet).some(
      ([key, value]) => value && !nextHasSpecialDiet[key]
    );

    if (showSpecialDietSelected) {
      this.toasterService.addMessageToQueue({
        id: COMMON_TOASTER_ID,
        contentKey: 'ancillaries.specialDiet.toastMessages.selectedSpecialMeal',
      });
    }

    if (showSpecialDietUnselected) {
      this.toasterService.addMessageToQueue({
        id: COMMON_TOASTER_ID,
        contentKey: 'ancillaries.specialDiet.toastMessages.unSelectedSpecialMeal',
      });
    }
  }

  private isRadioDisplay(category: Category = this.category): boolean {
    return [Category.WIFI, Category.MEAL].includes(category);
  }

  private setupTravelersAndServices(
    baseStream$: Observable<[Category, FinnairItineraryItemFlight | FinnairBoundItem]>,
    filteredTravelers$: Observable<ServicePassenger[]>
  ): void {
    const selectionsForFragmentAndCategory$ = baseStream$.pipe(
      switchMap(([category, flightOrBound]) => {
        return this.store$.pipe(serviceSelectionsForFragment(flightOrBound.id, category));
      })
    );

    const originalQuota = Object.values(snapshot(selectionsForFragmentAndCategory$) ?? {}).filter((item) =>
      Array.isArray(item) ? item.length > 0 : !!item
    ).length;

    this.travelersAndServices$ = combineLatest([
      this.services$,
      filteredTravelers$,
      this.includedServices$,
      this.combinedServices$,
      this.showConfirmationModalBeforeSelectionForPassengers$,
      this.showConfirmationModalBeforeSelectionRemovalForPassengers$,
      this.blockSelectionNotification$,
      this.travelers$,
      baseStream$,
    ]).pipe(
      map(
        ([
          services,
          travelers,
          includedServices,
          combinedServices,
          showConfirmationModalBeforeSelectionForPassengers,
          showConfirmationModalBeforeSelectionRemovalForPassengers,
          blockSelectionNotification,
          finnairPassengers,
          [category, flightOrBound],
        ]) => {
          if (Object.values(services ?? {}).length === 0) {
            return [];
          }

          return travelers.map((traveler: ServicePassenger) => {
            const selection$: Observable<TravelerSelections> = this.form.controls[traveler.id]?.valueChanges.pipe(
              startWith(this.form.controls[traveler.id].getRawValue()),
              finShare()
            );

            const includedServicesForTravelerAndFragment = findServiceItemsForPassengerForFragment(
              category,
              includedServices,
              traveler.id,
              flightOrBound.id
            );

            const combinedGroupedServices = getGroupedServices(
              findServiceItemsForPassengerForFragment(
                this.category,
                combinedServices,
                traveler.id,
                this.flightOrBound.id,
                false
              ),
              getPassengerTierLevel(traveler.id, finnairPassengers)
            );

            const servicesToDisplay =
              includedServicesForTravelerAndFragment.length > 0 && this.isRadioDisplay()
                ? (services?.[traveler.id] ?? []).filter(
                    (s) =>
                      !!includedServicesForTravelerAndFragment.find(({ variant, totalPrice, subCategory }) => {
                        return (
                          variant === s.variant ||
                          (totalPrice?.amount === '0' && subCategory === SubCategory.SPECIAL_DIET)
                        );
                      })
                  )
                : (services?.[traveler.id] ?? []);

            const extendedServices = this.getExtendedServices(
              traveler,
              servicesToDisplay,
              selection$,
              originalQuota,
              showConfirmationModalBeforeSelectionForPassengers,
              blockSelectionNotification,
              selectionsForFragmentAndCategory$,
              includedServicesForTravelerAndFragment,
              category,
              flightOrBound
            );

            const selectedServices$: Observable<ExtendedService[]> = this.getSelectedServices(
              selection$,
              extendedServices
            );

            const filters = this.getFilters(extendedServices);
            const selectedFilter$ = new BehaviorSubject(
              filters.length === 0 ? 'all' : filters.length > 1 ? filters[0] : 'none'
            );
            return {
              traveler,
              filters,
              selectedFilter$,
              combinedGroupedServices,
              services: extendedServices,
              selectedServices$,
              hasServiceSelected$: this.getHasServiceSelected(
                selection$,
                includedServicesForTravelerAndFragment,
                category
              ),
              additionalInfoBlockUrl: this.getAdditionalInfoBlock(extendedServices, category),
              useConfirmModalBeforeSelectionRemoval: showConfirmationModalBeforeSelectionRemovalForPassengers?.includes(
                traveler.id
              ),
            } as TravelerServices;
          });
        }
      ),
      finShare()
    );
  }

  private getHasServiceSelected(
    selection$: Observable<TravelerSelections>,
    includedServicesForTravelerAndFragment: FinnairPassengerServiceSelectionItem[],
    category: Category
  ): Observable<boolean> {
    return selection$.pipe(
      map((selection) => {
        if (typeof selection['radio'] === 'string') {
          const alreadyPaid = !!includedServicesForTravelerAndFragment.find(
            (service) => service.variant === selection['radio']
          );
          return selection['radio'] !== CONFIG[category].notSelectedVariant || alreadyPaid;
        }
        return Object.keys(selection).some((variant) => {
          const alreadyPaid = !!includedServicesForTravelerAndFragment.find((service) => service.variant === variant);

          const travellerSelection = selection[variant];

          return (
            (typeof travellerSelection === 'number' ? travellerSelection > 0 : toBoolean(travellerSelection)) ||
            alreadyPaid
          );
        });
      })
    );
  }

  private getFilters(extendedServices: ExtendedService[]): SubCategoryFilter[] {
    if (this.category !== Category.MEAL) {
      return [];
    }
    const baseFilters = extendedServices.some((s) => s.subCategory === SubCategory.SPECIAL_DIET)
      ? extendedServices.flatMap((s) => s.subCategory).filter(unique)
      : [];

    return baseFilters.length > 1 ? [...baseFilters, 'all'] : baseFilters;
  }

  private getSelectedServices(
    selection$: Observable<TravelerSelections>,
    extendedServices: ExtendedService[]
  ): Observable<ExtendedService[]> {
    return selection$.pipe(
      map((selection) => {
        return extendedServices
          .filter((service) => {
            if (service.maxNumberItems > 1) {
              return Number(selection[service.variant]) > 0;
            }
            if (this.isRadioDisplay()) {
              return service.variant === selection['radio'] && !NO_VARIANTS.includes(selection['radio']);
            }
            return toBoolean(selection[service.variant]);
          })
          .map((service) => {
            if (typeof selection[service.variant] === 'number') {
              return {
                ...service,
                quantity: Number(selection[service.variant]),
                totalPrice: {
                  ...service.totalPrice,
                  amount: PreciseNumber.fromString(service.totalPrice.amount)
                    .multiply(PreciseNumber.fromString(String(selection[service.variant])))
                    .toString(),
                },
                ...(service.originalPrice && {
                  originalPrice: {
                    ...service.originalPrice,
                    amount: PreciseNumber.fromString(service.originalPrice.amount)
                      .multiply(PreciseNumber.fromString(String(selection[service.variant])))
                      .toString(),
                  },
                }),
              };
            }
            return { ...service, quantity: 1 };
          })
          .filter((service) => service.paidQuantity === 0 || +service.totalPrice?.amount === 0);
      })
    );
  }

  private getExtendedServices(
    traveler: ServicePassenger,
    services: ServiceCatalogService[],
    selection$: Observable<TravelerSelections>,
    originalQuota: number,
    showConfirmationModalBeforeSelectionForPassengers: string[],
    blockSelectionNotification: string[],
    selectionsForFragmentAndCategory$: Observable<SelectionsPerTraveler<unknown>>,
    includedServicesForTravelerAndFragment: FinnairPassengerServiceSelectionItem[],
    category: Category,
    flightOrBound: FinnairItineraryItemFlight | FinnairBoundItem
  ): ExtendedService[] {
    const selectionQuota$ = selectionsForFragmentAndCategory$.pipe(
      map(
        (selections) =>
          Object.values(selections).filter((item) => (Array.isArray(item) ? item.length > 0 : !!item)).length
      ),
      finShare()
    );

    return services
      .map((service: ServiceCatalogService) => {
        const paidQuantity =
          includedServicesForTravelerAndFragment.find((s) => s.variant === service.variant)?.quantity ?? 0;
        const selected$ = this.getIsSelectedStream(selection$, service, paidQuantity);
        const displayTotalPrice$ = selection$.pipe(
          map((selection) => {
            const count = service.maxNumberItems > 1 ? Number(selection[service.variant]) : 1;

            return {
              amount: PreciseNumber.fromString(service.totalPrice.amount)
                .multiply(PreciseNumber.from(count, 0))
                .toString(),
              currencyCode: service.totalPrice.currencyCode,
            };
          })
        );
        const inputType = this.getInputType(service);

        return {
          ...service,
          paidQuantity,
          displayTotalPrice$,
          selected$,
          inputType,
          useConfirmModal: service.isService && showConfirmationModalBeforeSelectionForPassengers.includes(traveler.id),
          showBlockingNotification: service.isService && blockSelectionNotification.includes(traveler.id),
          quota$: combineLatest([selected$, selectionQuota$]).pipe(
            map(([isSelected, selectionQuota]) =>
              this.calculateQuota(selectionQuota, service, isSelected, originalQuota)
            ),
            finShare()
          ),
          paxLevelQuotaReached$: this.getPaxLevelQuotaReached(service.variant, this.flightOrBound.id, traveler.id),
          globalId: `${traveler.id}-${category}-${flightOrBound.id}-${service.variant}`,
          buttonText$: selected$.pipe(
            switchMap((selected) => {
              return this.languageService.translateMultiple(
                [
                  `ancillaries.${service.category}.${service.variant}.${selected ? 'selected' : 'select'}`,
                  `ancillaries.${service.category}.${selected ? 'selected' : 'select'}`,
                ],
                undefined,
                { logErrors: false }
              );
            }),
            map((translations) => translations[0] || translations[1])
          ),
        };
      })
      .filter((s) => s.isService);
  }

  removeSelections(event: Event, travelerId: string, flightOrBoundId: string, useConfirmModal: boolean): void {
    if (useConfirmModal) {
      event.preventDefault();
      this.openConfirmModalWithData.emit({
        flightOrBoundId: flightOrBoundId,
        travelerId: travelerId,
        removeSelection: true,
      });
    } else {
      if (this.form.controls[travelerId].controls['radio']) {
        this.form.controls[travelerId].controls['radio'].setValue('');
      }
    }
  }

  private getPaxLevelQuotaReached(variant: string, fragmentId: string, travelerId: string) {
    return combineLatest([this.paxLevelQuota$, this.serviceQuantityMap$]).pipe(
      map(([paxLevelQuota, quantity]) => {
        if (!paxLevelQuota.variants.includes(variant)) {
          return false;
        }
        return quantity[fragmentId]?.[travelerId] >= paxLevelQuota.combinedQuota;
      }),
      startWith(false),
      distinctUntilChanged()
    );
  }

  private calculateQuota(
    selectionQuota: number,
    service: ServiceCatalogService,
    isSelected: boolean,
    originalQuota: number
  ) {
    const quota = service.quota === null ? undefined : service.quota;
    const availableQuota = quota + originalQuota;
    const isQuotaToppedByCustomer = selectionQuota > 0 && selectionQuota >= availableQuota;
    const type = isSelected
      ? QuotaType.SELECTED
      : availableQuota === 0
        ? QuotaType.NO
        : isQuotaToppedByCustomer
          ? QuotaType.TOPPED
          : QuotaType.FREE;

    return {
      type,
      count: quota !== undefined ? availableQuota - selectionQuota : undefined,
    };
  }

  private getInputType(service: ServiceCatalogService): ProductInputType {
    return (
      this.isRadioDisplay(service.category) ? 'radio' : service.maxNumberItems > 1 ? 'number' : 'checkbox'
    ) as ProductInputType;
  }

  private getIsSelectedStream(
    selection$: Observable<TravelerSelections>,
    service: ServiceCatalogService,
    paidQuantity: number
  ): Observable<boolean> {
    return selection$.pipe(
      map((selection): boolean => {
        if (paidQuantity > 0 && (!selection['radio'] || selection['radio'] === service.variant)) {
          return true;
        }
        return this.isRadioDisplay(service.category)
          ? selection['radio'] === service.variant
          : toBoolean(selection[service.variant]);
      }),
      finShare()
    );
  }
}
